import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Phone, MessageCircle } from "lucide-react";
import { api, imgUrl, formatPrice } from "@/lib/api";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { ProductCard } from "@/components/ProductCard";
import { QuickView } from "@/components/QuickView";

const WHATSAPP =
  "https://wa.me/919570216659?text=Hello%20THRIFTHOUSE,%20I'm%20interested%20in%20your%20curated%20collection.";

const SectionHeading = ({ eyebrow, title, children }) => (
  <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
    <div>
      <p className="font-mono-th text-xs uppercase tracking-[0.3em] text-neutral-500 mb-3">{eyebrow}</p>
      <h2 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">{title}</h2>
    </div>
    {children}
  </div>
);

export default function Home() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [quick, setQuick] = useState(null);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/products?newArrivals=true").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/products").then(({ data }) => setFeatured(data.filter((p) => p.isFeatured).slice(0, 3))).catch(() => {});
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 360, behavior: "smooth" });
  };

  const lead = featured[0];

  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />

      {/* NEW STOCK */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28" data-testid="new-stock-section">
        <SectionHeading eyebrow="Just Dropped" title="New Stock">
          <div className="flex items-center gap-3">
            <button onClick={() => scroll(-1)} className="w-11 h-11 border border-white/20 flex items-center justify-center hover:bg-white/5" data-testid="carousel-prev" aria-label="Scroll left">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button onClick={() => scroll(1)} className="w-11 h-11 border border-white/20 flex items-center justify-center hover:bg-white/5" data-testid="carousel-next" aria-label="Scroll right">
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </SectionHeading>

        <div ref={scrollRef} className="flex gap-6 overflow-x-auto scroll-fade pb-4 -mx-5 px-5 md:mx-0 md:px-0">
          {products.map((p, i) => (
            <div key={p.id} className="min-w-[260px] md:min-w-[300px] w-[260px] md:w-[300px] shrink-0">
              <ProductCard product={p} onQuickView={setQuick} index={i} />
            </div>
          ))}
          {products.length === 0 && (
            <p className="text-neutral-500 font-mono-th text-sm">No new stock yet. Check back soon.</p>
          )}
        </div>
      </section>

      {/* CATEGORIES */}
      <section id="categories" className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28" data-testid="categories-section">
        <SectionHeading eyebrow="Browse By" title="Categories" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {categories.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.07 }}
              onClick={() => navigate(`/shop?category=${encodeURIComponent(c.name)}`)}
              className={`group relative overflow-hidden cursor-pointer ${i === 0 ? "col-span-2 aspect-square md:aspect-auto" : "aspect-[4/5]"}`}
              data-testid={`category-${c.name.toLowerCase()}`}
            >
              <img src={imgUrl(c.image)} alt={c.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors" />
              <div className="absolute inset-0 flex items-end p-5">
                <div>
                  <h3 className="font-display font-bold text-xl md:text-2xl uppercase tracking-wide">{c.name}</h3>
                  <span className="font-mono-th text-[10px] uppercase tracking-[0.2em] text-neutral-300 flex items-center gap-2 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    Shop now <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      {lead && (
        <section className="py-20 md:py-28 bg-[#0e0f12] border-y border-white/10" data-testid="featured-section">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10">
            <SectionHeading eyebrow="Editorial" title="Featured" />
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="aspect-[4/5] overflow-hidden bg-[#1A1D24]"
              >
                <img src={imgUrl(lead.image)} alt={lead.name} className="w-full h-full object-cover" />
              </motion.div>
              <div>
                <p className="font-mono-th text-xs uppercase tracking-[0.3em] text-neutral-500">{lead.category}</p>
                <h3 className="font-display font-black text-3xl md:text-5xl uppercase mt-3 leading-none">{lead.name}</h3>
                <p className="text-neutral-400 mt-5 leading-relaxed max-w-md">{lead.description}</p>
                <p className="font-display font-bold text-2xl mt-6">{formatPrice(lead.discountPrice || lead.price)}</p>
                <button
                  onClick={() => navigate(`/product/${lead.id}`)}
                  className="mt-8 group inline-flex items-center gap-3 bg-white text-black px-8 py-4 font-mono-th text-xs uppercase tracking-[0.2em] font-semibold hover:gap-5 transition-all"
                  data-testid="featured-view-product-btn"
                >
                  View Product <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* BRAND STORY */}
      <section className="relative py-28 md:py-40 overflow-hidden grain" data-testid="brand-section">
        <div className="absolute inset-0 opacity-25">
          <img src="https://images.pexels.com/photos/19463203/pexels-photo-19463203.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C0E] via-[#0B0C0E]/70 to-[#0B0C0E]" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-10 text-center">
          <p className="font-mono-th text-xs uppercase tracking-[0.3em] text-neutral-500 mb-6">The THRIFTHOUSE Ethos</p>
          <h2 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
            Not fast fashion.<br />A curated rebellion.
          </h2>
          <p className="text-neutral-300 mt-8 leading-relaxed text-base md:text-lg">
            THRIFTHOUSE hand-selects every piece where luxury streetwear meets the soul of vintage thrift.
            Limited runs, distinct silhouettes, and garments with character — made for those who refuse
            to look like everyone else. Each drop is a chapter. You decide the story.
          </p>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28" data-testid="contact-section">
        <div className="glass p-8 md:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
          <div>
            <p className="font-mono-th text-xs uppercase tracking-[0.3em] text-neutral-500 mb-4">Get in touch</p>
            <h2 className="font-display font-black uppercase text-3xl md:text-5xl leading-none tracking-tight">
              Questions? <br className="hidden md:block" />Order on WhatsApp.
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <a href="tel:9570216659" className="flex items-center gap-3 border border-white/15 px-5 py-3 font-mono-th text-sm hover:bg-white/5" data-testid="contact-phone-1">
                <Phone className="w-4 h-4" /> 9570216659
              </a>
              <a href="tel:9508859162" className="flex items-center gap-3 border border-white/15 px-5 py-3 font-mono-th text-sm hover:bg-white/5" data-testid="contact-phone-2">
                <Phone className="w-4 h-4" /> 9508859162
              </a>
            </div>
          </div>
          <a
            href={WHATSAPP}
            target="_blank"
            rel="noreferrer"
            className="group inline-flex items-center gap-3 bg-emerald-500 text-black px-8 py-5 font-mono-th text-xs uppercase tracking-[0.2em] font-bold hover:bg-emerald-400 hover:gap-5 transition-all shrink-0"
            data-testid="contact-whatsapp-btn"
          >
            <MessageCircle className="w-5 h-5" /> Chat on WhatsApp
          </a>
        </div>
      </section>

      <QuickView product={quick} onClose={() => setQuick(null)} />
    </div>
  );
}
